import { useEffect, useMemo, useState } from 'react';
import type { MinigameViewProps } from '../../types/minigame';

// Packet Routing (level 15) — pipe puzzle. Rotate the cabling until a route runs from the
// ENTRY (top-left) to the CORE (bottom-right), inside the TTL budget, without crossing an
// IDS node. Cemented nodes can't be rotated at all.
//
// The connectivity read-out below is a local preview computed with the same rules as the
// server; the server re-checks the whole board on submit (locked cells untouched, TTL spent,
// route exists) and its verdict is the one that counts.

interface Challenge {
  size: number;
  /** Canonical connection mask per cell (up=1, right=2, down=4, left=8). */
  kinds: number[];
  orientations: number[];
  locked: number[];
  /** Empty when the player lacks the tool — then the IDS nodes stay hidden. */
  ids: number[];
  showIds: boolean;
  budget: number;
  timeLimitSec: number;
  entry: number;
  core: number;
}

const UP = 1, RIGHT = 2, DOWN = 4, LEFT = 8;

/** Rotate a connection mask clockwise `steps` times — mirrors the server. */
function rotate(mask: number, steps: number): number {
  let m = mask;
  for (let i = 0; i < ((steps % 4) + 4) % 4; i++) m = ((m << 1) | (m >> 3)) & 0b1111;
  return m;
}

function neighbours(cell: number, size: number): [number, number][] {
  const x = cell % size, y = Math.floor(cell / size);
  const out: [number, number][] = [];
  if (y > 0)        out.push([UP, cell - size]);
  if (x < size - 1) out.push([RIGHT, cell + 1]);
  if (y < size - 1) out.push([DOWN, cell + size]);
  if (x > 0)        out.push([LEFT, cell - 1]);
  return out;
}

const opposite = (dir: number) => (dir === UP ? DOWN : dir === DOWN ? UP : dir === LEFT ? RIGHT : LEFT);

/** Cells reachable from the entry, treating IDS nodes as impassable (same rule as the server). */
function reachable(size: number, kinds: number[], orient: number[], ids: Set<number>): Set<number> {
  const seen = new Set<number>([0]);
  const queue = [0];
  const core = size * size - 1;
  while (queue.length) {
    const cur = queue.shift()!;
    const mask = rotate(kinds[cur], orient[cur]);
    for (const [dir, next] of neighbours(cur, size)) {
      if (seen.has(next)) continue;
      if (ids.has(next) && next !== core) continue;
      if ((mask & dir) === 0) continue;
      if ((rotate(kinds[next], orient[next]) & opposite(dir)) === 0) continue;
      seen.add(next);
      queue.push(next);
    }
  }
  return seen;
}

export default function PacketRoutingView({ challenge, onSubmit, setPendingSubmission, busy }: MinigameViewProps<Challenge>) {
  const size = challenge?.size ?? 5;
  const kinds = useMemo(() => challenge?.kinds ?? [], [challenge]);
  const initial = useMemo(() => challenge?.orientations ?? [], [challenge]);
  const locked = useMemo(() => new Set(challenge?.locked ?? []), [challenge]);
  const ids = useMemo(() => new Set(challenge?.ids ?? []), [challenge]);
  const budget = challenge?.budget ?? 0;
  const core = challenge?.core ?? size * size - 1;

  const [orient, setOrient] = useState<number[]>(initial);

  // Fresh board on a new attempt.
  useEffect(() => {
    setOrient(initial);
    setPendingSubmission({ orientations: initial });
  }, [initial, setPendingSubmission]);

  // TTL is charged on the NET clockwise rotation of each cell, exactly as the server does —
  // so undoing a mistake costs its shortest way round, not every click.
  const used = orient.reduce((sum, o, i) => sum + (((o - (initial[i] ?? 0)) % 4) + 4) % 4, 0);

  const lit = reachable(size, kinds, orient, ids);
  const connected = lit.has(core);
  const overBudget = used > budget;

  const click = (cell: number) => {
    if (busy || locked.has(cell)) return;
    const next = [...orient];
    next[cell] = (next[cell] + 1) % 4;
    setOrient(next);
    setPendingSubmission({ orientations: next });
  };

  const cellStyle = (i: number): React.CSSProperties => {
    const isLocked = locked.has(i);
    const isIds = ids.has(i);
    const isLit = lit.has(i);
    return {
      position: 'relative', aspectRatio: '1 / 1', minWidth: 0, padding: 0,
      background: isIds ? '#2a0f14' : isLocked ? '#15181d' : '#0d1117',
      border: `1px solid ${isIds ? '#f87171' : isLocked ? '#5b6674' : isLit ? '#4ade80' : '#232833'}`,
      borderRadius: 3,
      cursor: busy || isLocked ? 'not-allowed' : 'pointer',
      boxShadow: isLit ? 'inset 0 0 8px rgba(74,222,128,0.18)' : 'none',
      transition: 'border-color 0.12s, box-shadow 0.12s',
    };
  };

  /** The four arms of a pipe, drawn as bars so there's no font dependency. */
  const arms = (i: number) => {
    const mask = rotate(kinds[i] ?? 0, orient[i] ?? 0);
    const on = lit.has(i);
    const col = ids.has(i) ? '#f87171' : on ? '#4ade80' : '#4b5563';
    const T = 3;   // bar thickness, px
    const bar = (dir: number): React.CSSProperties | null => {
      if ((mask & dir) === 0) return null;
      const base: React.CSSProperties = { position: 'absolute', background: col, borderRadius: 1 };
      if (dir === UP)    return { ...base, left: `calc(50% - ${T / 2}px)`, top: 0, width: T, height: '50%' };
      if (dir === DOWN)  return { ...base, left: `calc(50% - ${T / 2}px)`, bottom: 0, width: T, height: '50%' };
      if (dir === LEFT)  return { ...base, top: `calc(50% - ${T / 2}px)`, left: 0, height: T, width: '50%' };
      return { ...base, top: `calc(50% - ${T / 2}px)`, right: 0, height: T, width: '50%' };
    };
    return (
      <>
        {[UP, RIGHT, DOWN, LEFT].map(d => {
          const st = bar(d);
          return st ? <span key={d} style={st} /> : null;
        })}
        <span style={{
          position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%,-50%)',
          width: 6, height: 6, borderRadius: '50%', background: col, opacity: 0.9,
        }} />
      </>
    );
  };

  const badge = (i: number) => {
    if (i === 0) return <Tag text="IN" color="#38bdf8" />;
    if (i === core) return <Tag text="CORE" color="#fbbf24" />;
    if (locked.has(i)) return <Tag text="🔒" color="#94a3b8" />;
    if (ids.has(i)) return <Tag text="IDS" color="#f87171" />;
    return null;
  };

  return (
    <div style={{ fontFamily: 'monospace' }}>
      <p style={{ color: '#cbd5e1', fontSize: '0.82rem', margin: '0 0 0.6rem', lineHeight: 1.5 }}>
        Click a node to rotate its cabling and link <strong style={{ color: '#38bdf8' }}>IN</strong> (top-left) to{' '}
        <strong style={{ color: '#fbbf24' }}>CORE</strong> (bottom-right). Each net rotation costs{' '}
        <strong>1 TTL</strong>. <span style={{ color: '#94a3b8' }}>🔒 Cemented</span> nodes can't be rotated
        {challenge?.showIds
          ? <>, and a route through an <span style={{ color: '#f87171' }}>IDS</span> node is detected — go around it.</>
          : <>. Your FTP Exploit is too weak to reveal the IDS nodes: route carefully.</>}
      </p>

      <div style={{ display: 'flex', gap: '1.2rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '0.6rem', fontSize: '0.82rem' }}>
        <span>
          <span style={{ color: 'var(--text-muted)' }}>TTL </span>
          <strong style={{ color: overBudget ? '#f87171' : used > budget * 0.6 ? '#fbbf24' : '#e2e8f0' }}>{used}</strong>
          <span style={{ color: 'var(--text-muted)' }}> / {budget}</span>
        </span>
        <span style={{ color: connected ? '#4ade80' : '#6b7280' }}>
          {connected ? '● route established' : '○ route broken'}
        </span>
      </div>

      <div style={{
        display: 'grid', gridTemplateColumns: `repeat(${size}, minmax(0, 1fr))`,
        gap: '0.3rem', maxWidth: `${size * 3.4}rem`, marginBottom: '0.8rem',
      }}>
        {Array.from({ length: size * size }, (_, i) => (
          <button key={i} onClick={() => click(i)} disabled={busy || locked.has(i)} style={cellStyle(i)}>
            {arms(i)}
            {badge(i)}
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <button
          className="primary"
          onClick={() => onSubmit({ orientations: orient })}
          disabled={busy}
          style={{ fontSize: '0.82rem', padding: '0.35rem 0.9rem' }}
        >
          {busy ? '…' : 'Send packet'}
        </button>
        {overBudget && (
          <span style={{ color: '#f87171', fontSize: '0.75rem' }}>Over TTL — the packet will die in transit.</span>
        )}
        {!overBudget && connected && (
          <span style={{ color: '#4ade80', fontSize: '0.75rem' }}>Route is clear — send it before the clock runs out.</span>
        )}
      </div>
    </div>
  );
}

function Tag({ text, color }: { text: string; color: string }) {
  return (
    <span style={{
      position: 'absolute', top: 1, left: 2, fontSize: '0.5rem', lineHeight: 1,
      color, fontWeight: 'bold', pointerEvents: 'none',
    }}>
      {text}
    </span>
  );
}
